import { useCallback, useEffect, useState } from "react";
import { History, Loader2, Plus, Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { fetchCatalogUnits } from "@/lib/catalogApi";
import type { CatalogUnit } from "@/lib/types";
import {
  createNegotiationVersion,
  deleteNegotiationVersion,
  fetchEffectiveNegotiation,
  fetchNegotiationHistory,
  fetchNegotiationVersions,
  updateNegotiationVersion,
  type EffectiveNegotiation,
  type NegotiationHistoryEntry,
  type NegotiationVersion,
} from "@/lib/adminNegotiationApi";
import { blankForm, buildPayload, toForm, type FormState, type TaxDraft } from "@/lib/negotiationForm";
import { NegotiationPriceTablePanel } from "@/components/admin/NegotiationPriceTablePanel";
import { brToday } from "@/lib/brDate";

const pct = (v: number | null) => (v == null ? "—" : `${Number(v)}%`);
const originLabel: Record<string, string> = {
  UNIDADE: "unidade", CLIENTE: "cliente", PADRAO: "padrão", NAO_CADASTRADA: "não cadastrado",
};

/** Escopo cliente (unitId null) ou exceção de unidade de entrega (unitId preenchido). */
export function NegotiationConditionPanel({
  customerId,
  unitId = null,
  canEdit,
}: {
  customerId: string;
  unitId?: string | null;
  canEdit: boolean;
}) {
  const isUnit = unitId != null;
  const [versions, setVersions] = useState<NegotiationVersion[]>([]);
  const [effective, setEffective] = useState<EffectiveNegotiation | null>(null);
  const [units, setUnits] = useState<CatalogUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState<string>(() => brToday());
  const [history, setHistory] = useState<NegotiationHistoryEntry[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  /** Recarrega versões + condição efetiva para a data informada (ou a selecionada). */
  const load = useCallback(async (dateOverride?: string) => {
    setLoading(true);
    setError(null);
    const date = dateOverride ?? effectiveDate;
    try {
      const [vs, eff, us] = await Promise.all([
        fetchNegotiationVersions(customerId, unitId),
        fetchEffectiveNegotiation(customerId, unitId, date),
        fetchCatalogUnits(),
      ]);
      setVersions(vs);
      setEffective(eff);
      setUnits(us);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar condições da negociação");
    } finally {
      setLoading(false);
    }
  }, [customerId, unitId, effectiveDate]);

  useEffect(() => { void load(); }, [load]);

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setHistory(await fetchNegotiationHistory(customerId, unitId));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao carregar o histórico");
    } finally {
      setHistoryLoading(false);
    }
  }, [customerId, unitId]);

  const submit = async () => {
    if (!form || busy) return; // bloqueia cliques repetidos durante a gravação
    let payload;
    try {
      payload = buildPayload(form);
    } catch (e) {
      // entrada decimal inválida/ambígua → mensagem clara, sem enviar (preenchimento preservado)
      toast.error(e instanceof Error ? e.message : "Valor numérico inválido");
      return;
    }
    if (!payload) {
      toast.error("Preencha o início de vigência.");
      return;
    }
    setBusy(true);
    try {
      if (form.id) {
        await updateNegotiationVersion(customerId, unitId, form.id, payload);
      } else {
        await createNegotiationVersion(customerId, unitId, payload);
      }
      toast.success("Condição salva.");
      setForm(null);                 // encerra o editor no sucesso
      await load();                  // recarrega versões + efetiva (data selecionada)
      if (history !== null) await refreshHistory(); // mantém o histórico sincronizado se aberto
    } catch (e) {
      // erro → preserva o preenchimento do formulário (não fecha)
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Falha ao salvar";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (v: NegotiationVersion) => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteNegotiationVersion(customerId, unitId, v.id);
      toast.success("Condição excluída.");
      await load();
      if (history !== null) await refreshHistory();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Falha ao excluir");
    } finally {
      setBusy(false);
    }
  };

  const toggleHistory = async () => {
    if (history !== null) { setHistory(null); return; }
    await refreshHistory();
  };

  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Carregando...</div>;
  }
  if (error) {
    return <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Condição efetiva (data selecionada) com origem de cada campo */}
      <div className="rounded-xl border border-border bg-secondary/20 p-4 text-sm">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="font-semibold">Condição efetiva em</span>
          <Input type="date" value={effectiveDate} disabled={busy}
            onChange={(e) => { setEffectiveDate(e.target.value); void load(e.target.value); }}
            className="h-8 w-40 text-xs" />
        </div>
        {!effective?.configured ? (
          <div className="text-muted-foreground">{effective?.message ?? "Negociação não cadastrada"}</div>
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            <Eff label="Comissão do cliente" rv={effective.clientCommissionPercent} />
            <Eff label="Comissão do intermediário" rv={effective.intermediaryCommissionPercent}
              extra={effective.intermediaryCommissionBase
                ? ` (base ${baseLabel(effective.intermediaryCommissionBase)}, ${originLabel[effective.intermediaryCommissionBaseOrigin] ?? ""})`
                : ""} />
            <Eff label="Margem desejada" rv={effective.targetMarginPercent} />
            <Eff label="Margem mínima" rv={effective.minimumMarginPercent} />
            <div className="md:col-span-2 text-xs text-muted-foreground">
              Impostos/despesas: {effective.taxes.length} componente(s) — origem {originLabel[effective.taxListOrigin] ?? "—"}
              {effective.taxListState === "DEFINIDA" && effective.taxes.length === 0
                ? " (sem impostos/despesas adicionais considerados)" : ""}
            </div>
            {effective.intermediaryIncompleteAfterClientCommission && (
              <div className="md:col-span-2 rounded bg-warning/15 px-2 py-1 text-xs text-warning">
                Base do intermediário é "após comissão do cliente", mas a comissão do cliente efetiva está ausente — configuração incompleta.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Versões */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">Versões por vigência</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void toggleHistory()} disabled={historyLoading}>
            {historyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
            {history !== null ? "Ocultar histórico" : "Histórico"}
          </Button>
          {canEdit && !form && (
            <Button size="sm" variant="outline" onClick={() => setForm(blankForm(isUnit))}>
              <Plus className="h-4 w-4" /> Nova condição
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {versions.length === 0 && <div className="text-xs text-muted-foreground">Nenhuma versão cadastrada.</div>}
        {versions.map((v) => (
          <div key={v.id} className="rounded-lg border border-border p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={v.status} />
              <span className="tabular-nums">{v.validFrom} → {v.validTo ?? "em aberto"}</span>
              <span className="text-muted-foreground">
                cliente {pct(v.clientCommissionPercent)} · interm. {pct(v.intermediaryCommissionPercent)}
                · meta {pct(v.targetMarginPercent)} · mín. {pct(v.minimumMarginPercent)}
              </span>
              {canEdit && (
                <span className="ml-auto flex gap-1">
                  <Button size="sm" variant="ghost" aria-label="Editar condição" onClick={() => setForm(toForm(v))}><Pencil className="h-4 w-4" /></Button>
                  {v.status === "FUTURA" && (
                    <Button size="sm" variant="ghost" aria-label="Excluir condição" onClick={() => void remove(v)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  )}
                </span>
              )}
            </div>
            <NegotiationPriceTablePanel conditionId={v.id} canEdit={canEdit} />
          </div>
        ))}
      </div>

      {form && canEdit && (
        <ConditionForm
          form={form}
          setForm={setForm}
          units={units}
          isUnit={isUnit}
          busy={busy}
          onSubmit={() => void submit()}
          onCancel={() => setForm(null)}
        />
      )}

      {history !== null && <HistoryView entries={history} units={units} />}
    </div>
  );
}

const money = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const actionLabel: Record<string, string> = { CREATE: "Criação", UPDATE: "Alteração", DELETE: "Exclusão" };
const fieldLabel: Record<string, string> = {
  validFrom: "Início de vigência", validTo: "Término de vigência",
  clientCommissionPercent: "Comissão do cliente", intermediaryCommissionPercent: "Comissão do intermediário",
  intermediaryCommissionBase: "Base do intermediário", targetMarginPercent: "Margem desejada",
  minimumMarginPercent: "Margem mínima", taxListMode: "Impostos/despesas", note: "Observação",
};
const percentFields = new Set([
  "clientCommissionPercent", "intermediaryCommissionPercent", "targetMarginPercent", "minimumMarginPercent",
]);

/** Valor legível de um campo do snapshot: null = herdar/não configurado; "0" = zero explícito. */
function fieldValue(key: string, raw: unknown): string {
  if (raw === null || raw === undefined) return "— (herdar/não configurado)";
  const s = String(raw);
  if (percentFields.has(key)) {
    const n = Number(s);
    return Number.isNaN(n) ? s : (n === 0 ? "0% (zero explícito)" : `${n}%`);
  }
  if (key === "intermediaryCommissionBase") {
    return s === "APOS_COMISSAO_CLIENTE" ? "após comissão do cliente" : s === "PRECO_FINAL" ? "preço final" : s;
  }
  if (key === "taxListMode") {
    return s === "DEFINIDA" ? "lista própria" : s === "HERDAR" ? "herdar do cliente" : s === "NAO_CONFIGURADA" ? "não configurada" : s;
  }
  return s;
}

function taxesSummary(raw: unknown, units: CatalogUnit[]): string {
  if (!Array.isArray(raw) || raw.length === 0) return "(sem componentes)";
  return (raw as Record<string, unknown>[]).map((t) => {
    const desc = String(t.description ?? "—");
    if (String(t.mode) === "PERCENT") {
      const base = t.percentBase === "APOS_COMISSAO_CLIENTE" ? "após com. cliente" : "preço final";
      return `${desc}: ${Number(t.percentValue ?? 0)}% (${base})`;
    }
    const scope = t.fixedScope === "POR_PRODUTO" ? "por produto" : t.fixedScope === "POR_PEDIDO" ? "por pedido" : "por entrega";
    const unit = t.fixedUnitId ? ` / ${units.find((u) => u.id === t.fixedUnitId)?.name ?? "unid."}` : "";
    const val = t.fixedValue != null ? money.format(Number(t.fixedValue)) : "—";
    return `${desc}: ${val} (${scope}${unit})`;
  }).join("; ");
}

function parseSnap(json: string | null): Record<string, unknown> | null {
  if (!json) return null;
  try { return JSON.parse(json) as Record<string, unknown>; } catch { return null; }
}

function HistoryView({ entries, units }: { entries: NegotiationHistoryEntry[]; units: CatalogUnit[] }) {
  if (entries.length === 0) {
    return <div className="rounded-lg border border-border p-3 text-xs text-muted-foreground">Sem histórico registrado.</div>;
  }
  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold">Histórico</div>
      {entries.map((h) => {
        const before = parseSnap(h.beforeJson);
        const after = parseSnap(h.afterJson);
        const keys = ["validFrom", "validTo", "clientCommissionPercent", "intermediaryCommissionPercent",
          "intermediaryCommissionBase", "targetMarginPercent", "minimumMarginPercent", "taxListMode", "note"];
        const changed = keys.filter((k) => {
          const b = before ? before[k] : undefined;
          const a = after ? after[k] : undefined;
          return (before && after) ? JSON.stringify(b) !== JSON.stringify(a) : true;
        });
        const taxesChanged = JSON.stringify(before?.taxes ?? null) !== JSON.stringify(after?.taxes ?? null);
        return (
          <div key={h.id} className="rounded-lg border border-border p-3 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-secondary px-1.5 py-0.5 font-bold uppercase">{actionLabel[h.action] ?? h.action}</span>
              <span className="text-muted-foreground">{new Date(h.changedAt).toLocaleString("pt-BR")}</span>
              <span className="text-muted-foreground">· {h.changedBy}</span>
              {h.scopeLabel && <span className="text-muted-foreground">· {h.scopeLabel}</span>}
            </div>
            <div className="mt-2 space-y-1">
              {(h.action === "UPDATE" ? changed : keys).map((k) => (
                <div key={k}>
                  <span className="text-muted-foreground">{fieldLabel[k] ?? k}: </span>
                  {h.action === "UPDATE" && before && after ? (
                    <span><span className="line-through opacity-70">{fieldValue(k, before[k])}</span> → <strong>{fieldValue(k, after[k])}</strong></span>
                  ) : (
                    <strong>{fieldValue(k, (after ?? before ?? {})[k])}</strong>
                  )}
                </div>
              ))}
              {(h.action !== "UPDATE" || taxesChanged) && (
                <div>
                  <span className="text-muted-foreground">Impostos/despesas: </span>
                  {h.action === "UPDATE" && before && after ? (
                    <span><span className="line-through opacity-70">{taxesSummary(before.taxes, units)}</span> → <strong>{taxesSummary(after.taxes, units)}</strong></span>
                  ) : (
                    <strong>{taxesSummary((after ?? before ?? {}).taxes, units)}</strong>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Eff({ label, rv, extra }: { label: string; rv: { value: number | null; origin: string; explicitZero: boolean }; extra?: string }) {
  return (
    <div>
      <span className="text-muted-foreground">{label}: </span>
      <span className="tabular-nums">{rv.value == null ? "—" : `${Number(rv.value)}%`}</span>
      <span className="ml-1 text-xs text-muted-foreground">({originLabel[rv.origin] ?? rv.origin}{rv.explicitZero ? ", zero explícito" : ""}){extra ?? ""}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    VIGENTE: "bg-primary/10 text-primary", FUTURA: "bg-warning/15 text-warning", ENCERRADA: "bg-secondary text-muted-foreground",
  };
  return <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold uppercase", map[status])}>{status}</span>;
}

function baseLabel(b: string): string {
  return b === "APOS_COMISSAO_CLIENTE" ? "após comissão do cliente" : "preço final";
}


function ConditionForm({
  form, setForm, units, isUnit, busy, onSubmit, onCancel,
}: {
  form: FormState;
  setForm: (f: FormState) => void;
  units: CatalogUnit[];
  isUnit: boolean;
  busy: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const set = (patch: Partial<FormState>) => setForm({ ...form, ...patch });
  const taxModes = isUnit ? ["HERDAR", "DEFINIDA"] : ["NAO_CONFIGURADA", "DEFINIDA"];

  const addTax = () => set({
    taxes: [...form.taxes, { description: "", mode: "PERCENT", percentValue: "", percentBase: "PRECO_FINAL",
      fixedValue: "", fixedScope: "POR_PEDIDO", fixedUnitId: null }],
  });
  const updateTax = (i: number, patch: Partial<TaxDraft>) =>
    set({ taxes: form.taxes.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) });
  const removeTax = (i: number) => set({ taxes: form.taxes.filter((_, idx) => idx !== i) });

  // Troca de modalidade: fixa defaults no ESTADO (casando com o visual dos seletores). O texto numérico
  // é preservado; o saneamento por modalidade ocorre no envio (draftToTax).
  const changeTaxMode = (i: number, mode: TaxDraft["mode"]) => {
    if (mode === "FIXO") {
      updateTax(i, { mode, fixedScope: form.taxes[i].fixedScope || "POR_PEDIDO",
        fixedUnitId: form.taxes[i].fixedScope === "POR_PRODUTO" ? form.taxes[i].fixedUnitId : null });
    } else {
      updateTax(i, { mode, percentBase: form.taxes[i].percentBase || "PRECO_FINAL" });
    }
  };

  return (
    <div className="space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-semibold">{form.id ? "Editar condição" : "Nova condição"}</span>
        <Button size="sm" variant="ghost" onClick={onCancel}><X className="h-4 w-4" /></Button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Início de vigência">
          <Input type="date" value={form.validFrom} onChange={(e) => set({ validFrom: e.target.value })} />
        </Field>
        <Field label="Término (vazio = em aberto)">
          <Input type="date" value={form.validTo} onChange={(e) => set({ validTo: e.target.value })} />
        </Field>
        <Field label={`Comissão do cliente % ${isUnit ? "(vazio = herdar)" : "(vazio = não configurado)"}`}>
          <Input inputMode="decimal" value={form.clientCommission} onChange={(e) => set({ clientCommission: e.target.value })} placeholder="ex. 20" />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Comissão interm. %">
            <Input inputMode="decimal" value={form.interPercent} onChange={(e) => set({ interPercent: e.target.value })} placeholder="ex. 10" />
          </Field>
          <Field label="Base interm.">
            <NativeSelect value={form.interBase} onChange={(v) => set({ interBase: v })}
              options={[["", "—"], ["PRECO_FINAL", "Preço final"], ["APOS_COMISSAO_CLIENTE", "Após comissão do cliente"]]} />
          </Field>
        </div>
        <Field label={`Margem desejada % ${isUnit ? "(vazio = herdar)" : ""}`}>
          <Input inputMode="decimal" value={form.target} onChange={(e) => set({ target: e.target.value })} />
        </Field>
        <Field label={`Margem mínima % ${isUnit ? "(vazio = herdar)" : "(mín. 10)"}`}>
          <Input inputMode="decimal" value={form.minimum} onChange={(e) => set({ minimum: e.target.value })} />
        </Field>
      </div>

      <Field label="Impostos/despesas">
        <NativeSelect value={form.taxListMode} onChange={(v) => set({ taxListMode: v })}
          options={taxModes.map((m) => [m, taxModeLabel(m)] as [string, string])} />
      </Field>

      {form.taxListMode === "DEFINIDA" && (
        <div className="space-y-2">
          {form.taxes.map((t, i) => (
            <div key={i} className="grid gap-2 rounded-lg border border-border p-2 md:grid-cols-[1.4fr_0.8fr_1fr_1fr_auto]">
              <Input placeholder="Descrição" value={t.description} onChange={(e) => updateTax(i, { description: e.target.value })} />
              <NativeSelect value={t.mode} onChange={(v) => changeTaxMode(i, v as TaxDraft["mode"])}
                options={[["PERCENT", "Percentual"], ["FIXO", "Valor fixo"]]} />
              {t.mode === "PERCENT" ? (
                <>
                  <Input placeholder="%" inputMode="decimal" value={t.percentValue} onChange={(e) => updateTax(i, { percentValue: e.target.value })} />
                  <NativeSelect value={t.percentBase || "PRECO_FINAL"} onChange={(v) => updateTax(i, { percentBase: v })}
                    options={[["PRECO_FINAL", "Preço final"], ["APOS_COMISSAO_CLIENTE", "Após com. cliente"]]} />
                </>
              ) : (
                <>
                  <Input placeholder="valor" inputMode="decimal" value={t.fixedValue} onChange={(e) => updateTax(i, { fixedValue: e.target.value })} />
                  <div className="grid grid-cols-1 gap-1">
                    <NativeSelect value={t.fixedScope || "POR_PEDIDO"} onChange={(v) => updateTax(i, { fixedScope: v, fixedUnitId: v === "POR_PRODUTO" ? t.fixedUnitId : null })}
                      options={[["POR_PRODUTO", "Por produto"], ["POR_PEDIDO", "Por pedido"], ["POR_ENTREGA", "Por entrega"]]} />
                    {t.fixedScope === "POR_PRODUTO" && (
                      <NativeSelect value={t.fixedUnitId ?? ""} onChange={(v) => updateTax(i, { fixedUnitId: v === "" ? null : v })}
                        options={[["", "Unidade..."], ...units.map((u) => [u.id, u.name] as [string, string])]} />
                    )}
                  </div>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={() => removeTax(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={addTax}><Plus className="h-4 w-4" /> Adicionar componente</Button>
        </div>
      )}

      <Field label="Observação">
        <Input value={form.note} onChange={(e) => set({ note: e.target.value })} />
      </Field>

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Cancelar</Button>
        <Button size="sm" variant="hero" onClick={onSubmit} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salvar condição
        </Button>
      </div>
    </div>
  );
}

function taxModeLabel(m: string): string {
  if (m === "HERDAR") return "Herdar a lista do cliente";
  if (m === "DEFINIDA") return "Definir lista (substitui)";
  return "Não configurada";
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function NativeSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
    >
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
